import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Users, Building2, Brain, Cog, Code, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Layers: Layers,
  Users: Users,
  Building2: Building2,
  Brain: Brain,
  Cog: Cog,
  Code: Code,
};

const ServiceCardSkeleton = () => (
  <div className="bg-white p-8 rounded-2xl border border-slate-100 animate-pulse">
    <div className="h-12 w-12 bg-slate-200 rounded-xl mb-6" />
    <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
    <div className="space-y-2">
      <div className="h-4 bg-slate-200 rounded w-full" />
      <div className="h-4 bg-slate-200 rounded w-5/6" />
    </div>
  </div>
);

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block text-primary font-medium mb-4">What We Do</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              Our Services
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Comprehensive digital engineering services to help your business thrive in the modern landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <ServiceCardSkeleton key={i} />)
            ) : (
              services.map((service, index) => {
                const Icon = iconMap[service.icon] || Layers;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/services/${service.slug}`} data-testid={`service-link-${service.slug}`}>
                      <div className="group h-full relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        <div className="relative z-10">
                          <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                            <Icon className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
                          </div>
                          
                          <h3 className="font-heading font-bold text-xl text-text-primary mb-3 group-hover:text-primary transition-colors">
                            {service.title}
                          </h3>
                          
                          <p className="text-text-secondary mb-6 line-clamp-3">
                            {service.description}
                          </p>
                          
                          <ul className="space-y-2 mb-6">
                            {service.features.slice(0, 3).map((feature, i) => (
                              <li key={i} className="flex items-center text-sm text-text-secondary">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary mr-2" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                          
                          <div className="flex items-center text-primary font-medium">
                            <span>Learn more</span>
                            <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
